import { useCallback, useEffect, useState } from "react";
import {
  getQaDesktopIdle,
  getQaDesktops,
  setQaDesktop,
  type QaDesktopHealth,
  type QaDesktopIdleView,
  type QaDesktopsView,
} from "../api/qaDesktops";

// What each health value means to an operator, and whether the machine can be
// picked at all. A machine that is not "ready" is refused HERE, with the reason
// next to it, so a switch can never look like it worked and then fail silently
// when the next job tries to drive a box that is not set up or not answering.
const HEALTH: Record<QaDesktopHealth, { text: string; selectable: boolean }> = {
  ready: { text: "ready", selectable: true },
  unprovisioned: { text: "not provisioned, cannot be selected", selectable: false },
  unreachable: { text: "unreachable, cannot be selected", selectable: false },
};

// Placeholder for the state before the first read lands. It is never rendered
// as an answer: the `loaded` gate below returns early until that read resolves.
const EMPTY: QaDesktopsView = { selected: "", desktops: [], configured: true };

// Reasons that mean THE GATE ITSELF IS BROKEN rather than "a human is working".
// Collapsing these into the in-use wording is the failure this whole line exists
// to prevent: the gate declines silently, so a probe that has been failing since
// yesterday looks exactly like a machine somebody is sitting at. `upstream_` is a
// family (the daemon builds it from whatever status the qa-agent returned), so it
// is matched by prefix instead of listed status by status.
const PROBE_BROKEN = /^(probe_failed|unreachable|upstream_)/;

function minutes(ms: number): number {
  return Math.floor(ms / 60_000);
}

/**
 * The one sentence that tells an operator why the desktop lane is quiet.
 *
 * Never keys on `reason` to decide "in use": a busy machine answers
 * `locked_out` and never `desktop_in_use`, because the qa-agent files the
 * lockout for the very input it is judging. `inUseNow` and `lockedOutUntil` are
 * the signals; `reason` only separates a broken probe from a working one.
 *
 * Returns "" when there is nothing to say, which is the whole render condition.
 */
function idleText(v: QaDesktopIdleView | null, nowMs: number): string {
  // Nothing was asked, so there is no state to report. Saying anything here
  // sends an operator to debug a gate that is simply not wired up.
  if (!v || !v.configured) return "";
  if (PROBE_BROKEN.test(v.reason ?? "")) return "the idle probe failed, so nothing will start";
  if (v.lockedOutUntil > nowMs) {
    // Ceil, not floor: reporting the last partial minute as gone would tell an
    // operator the machine frees up before it does.
    return `in use, ${Math.ceil((v.lockedOutUntil - nowMs) / 60_000)}m remaining`;
  }
  if (v.inUseNow) return "in use";
  // A null reading is not zero. Rendering "idle 0m" here would invent the one
  // number we specifically do not have.
  if (v.msSinceHumanInput === null) return "idle time unknown";
  return `idle ${minutes(v.msSinceHumanInput)}m, ${v.eligible ? "eligible" : "not eligible"}`;
}

/**
 * Which Windows machine Emil plays on.
 *
 * Sits under the Availability toggle and copies its shape: optimistic local
 * state on click, one read afterwards to reconcile, inert when the daemon has
 * no qa-agent configured. Machines are named by their owner label only, because
 * that is all the daemon sends: no ssh alias reaches the browser.
 */
export default function QaDesktopPicker() {
  const [view, setView] = useState<QaDesktopsView>(EMPTY);
  const [idle, setIdle] = useState<QaDesktopIdleView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: "warn" | "error"; text: string } | null>(null);

  // Read on mount and after a switch, never on a timer. Each read makes the
  // qa-agent probe every machine over ssh (up to 8s each) against boxes that may
  // be mid-run, so polling would hammer them for nothing. The idle read is worse
  // still: asking a machine somebody is using FILES A LOCKOUT as a side effect,
  // so a timer here would keep touching a box while its owner works on it.
  // "Refresh" below is the operator's deliberate re-read of both.
  const refresh = useCallback(async () => {
    try {
      // Both reads together, not one after the other: each carries its own 8s
      // daemon budget, so serialising them would turn Refresh into a 16s hang.
      // Settled rather than all, because the two answer different questions: a
      // failed idle read must not cost the operator the machine list, and a
      // failed list read must not hide why the lane is waiting.
      const [list, verdict] = await Promise.allSettled([getQaDesktops(), getQaDesktopIdle()]);
      if (list.status === "fulfilled") setView(list.value);
      else setNotice({ kind: "error", text: "The machine list could not be read." });
      // Only a dashboard-auth failure rejects here; every upstream problem comes
      // back as a value with a reason. Dropping the line is right in that case:
      // an idle state we could not read is exactly the unknown we must not dress
      // up as a number.
      setIdle(verdict.status === "fulfilled" ? verdict.value : null);
    } finally {
      // The first read is slow on purpose: it probes every machine over ssh
      // behind an 8s daemon timeout. Until it lands, EMPTY is a placeholder and
      // not an answer, so nothing downstream may read it as one.
      setLoaded(true);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const choose = useCallback(async (id: string) => {
    if (id === view.selected || busy) return;
    const previous = view;
    setBusy(true);
    setNotice(null);
    setView({
      ...view,
      selected: id,
      desktops: view.desktops.map((d) => ({ ...d, selected: d.id === id })),
    });
    try {
      const r = await setQaDesktop(id);
      // 200 and 202 both land here, and both mean the switch TOOK EFFECT.
      // `persisted: false` is the 202: it applied in memory but never reached
      // disk, so it reverts on restart. That is a warning about durability, not
      // a failed switch. Reporting it as an error would show a red state for a
      // working switch and leave the operator looking at the old machine.
      if (!r.persisted) {
        setNotice({
          kind: "warn",
          text: r.warning
            ?? `${r.label} is now the target, but the choice was not saved: it reverts to the previous machine when Emil restarts.`,
        });
      }
      await refresh();
    } catch (e) {
      // A refusal (a run still driving the machine, an unknown id) means nothing
      // changed upstream, so the previous choice still stands. Reverting locally
      // beats re-reading: the read would cost another round of ssh probes to
      // learn what we already know.
      setView(previous);
      setNotice({ kind: "error", text: (e as Error).message || "The switch was refused." });
    } finally {
      setBusy(false);
    }
  }, [view, busy, refresh]);

  // Before the first read lands there is no machine list, no `configured` answer
  // and no error, only a placeholder. Saying "no machines are configured" here
  // would be a false statement held for as long as the probe takes, and it sends
  // the operator off to check qa-agent config for a picker that was loading.
  if (!loaded) {
    return (
      <div className="qa-desktop qa-desktop-loading">
        <span className="qa-desktop-pill">QA machine</span>
        <span className="qa-desktop-note">Reading the machine list...</span>
      </div>
    );
  }

  if (!view.configured) {
    return (
      <div className="qa-desktop qa-desktop-unconfigured">
        <span className="qa-desktop-pill">QA machine: unavailable</span>
        <span className="qa-desktop-note">Emil is not configured</span>
      </div>
    );
  }

  // Computed at render, not stored: the lockout countdown is relative to now,
  // and a value frozen at fetch time would keep claiming minutes that have passed.
  const idleState = idleText(idle, Date.now());

  return (
    <div className="qa-desktop">
      <span className="qa-desktop-pill">QA machine</span>
      <div className="qa-desktop-options" role="radiogroup" aria-label="QA desktop machine">
        {view.desktops.map((d) => {
          const h = HEALTH[d.health];
          return (
            <label
              key={d.id}
              className={`qa-desktop-option${h.selectable ? "" : " qa-desktop-option-blocked"}`}
            >
              <input
                type="radio"
                name="qa-desktop"
                value={d.id}
                checked={d.id === view.selected}
                disabled={busy || !h.selectable}
                onChange={() => void choose(d.id)}
                aria-label={d.label}
                // The owner label names the control; the health is its
                // description, so a screen reader reads the reason a machine is
                // refused instead of just hearing a dimmed radio.
                aria-describedby={`qa-desktop-health-${d.id}`}
              />
              <span className="qa-desktop-label">{d.label}</span>
              <span className="qa-desktop-health" id={`qa-desktop-health-${d.id}`}>{h.text}</span>
            </label>
          );
        })}
      </div>
      <button
        type="button"
        className="qa-desktop-refresh"
        onClick={() => void refresh()}
        disabled={busy}
        aria-label="Re-read the QA machine list"
      >
        Refresh
      </button>
      {idleState && (
        // Why the desktop lane is quiet. The gate declines silently by design,
        // so without this line a probe that has been broken for a day is
        // indistinguishable from a machine somebody is sitting at. `label` is an
        // owner name and is the only machine identity the daemon sends; `reason`
        // is a free string from a process we do not control and is never
        // rendered, so no ssh alias can reach the browser through it.
        <span className="qa-desktop-note qa-desktop-idle" role="status">
          Automatic runs{idle?.label ? ` on ${idle.label}` : ""}: {idleState}
        </span>
      )}
      {view.error && (
        <span className="qa-desktop-note qa-desktop-error">
          Emil could not be reached, so this list may be stale.
        </span>
      )}
      {!view.error && view.desktops.length === 0 && (
        <span className="qa-desktop-note">No machines are configured for Emil.</span>
      )}
      {view.overrideActive && (
        // `overrideActive` is true when EITHER the visual or the audio host is
        // overridden, so it cannot tell us the visual lane is covered. Under an
        // audio-only override the visual lane still follows this picker, so the
        // banner reports the override instead of claiming the choice is dead.
        // The host is a display name and may be a label; it is rendered, never parsed.
        <span className="qa-desktop-note qa-desktop-override">
          An override is in force (forced host: {view.overrideHost ?? "unknown"}). Any lane it does
          not cover still follows the choice above.
        </span>
      )}
      {notice && (
        <span className={`qa-desktop-note qa-desktop-${notice.kind}`}>
          {notice.text}
        </span>
      )}
      <span className="qa-desktop-note">
        Global, and applies to the next job. A job already running keeps its machine.
      </span>
    </div>
  );
}
